/**
 * Mapbox GL wrapper: style/theme, vehicle puck, route rendering with live
 * congestion colouring, alternative routes, destination marker, and the
 * navigation camera loop.
 */

import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_TOKEN, REGION } from '../core/config';
import { loadStrippedStyle } from './styleLoader';
import type { Fix, LngLat, NavProgress, Route, TrackPoint } from '../core/types';
import { bounds, distance, lerpBearing } from '../core/geo';
import { routeLetter } from '../core/format';
import { computeCamera, previewPadding, type CameraPrefs } from './camera';

const SRC_ROUTE = 'wf-route';
const SRC_ALTS = 'wf-route-alts';
const SRC_DEST = 'wf-destination';
const SRC_PLACES = 'wf-places';
const SRC_DEM = 'wf-dem';
const SRC_DRIVEN = 'wf-driven';
const SRC_CRUMBS = 'wf-crumbs';
const SRC_TRIP = 'wf-trip-view';
const SRC_LETTERS = 'wf-route-letters';

export interface SavedPlaceMarker {
  id: string;
  name: string;
  lng: number;
  lat: number;
  kind: string;
  creatorName: string;
}

export interface MapViewEvents {
  onLongPress?: (point: LngLat) => void;
  onAltRouteClick?: (routeId: string) => void;
  /** User panned/rotated during navigation - switch to browse mode. */
  onUserGesture?: () => void;
  onPlaceClick?: (place: SavedPlaceMarker) => void;
}

type CongestionSegment = {
  coords: LngLat[];
  level: 'unknown' | 'low' | 'moderate' | 'heavy' | 'severe';
};

const ROUTE_BLUE = '#2f7dff';

/** Finger wobble tolerated during a long-press, in screen pixels. */
const LONG_PRESS_SLOP = 14;

const CONGESTION_COLORS: Record<CongestionSegment['level'], string> = {
  unknown: ROUTE_BLUE,
  low: ROUTE_BLUE,
  moderate: '#ffb020',
  heavy: '#ff6a45',
  severe: '#d7263d',
};

/**
 * Route line width as a function of zoom, scaled by `factor`.
 *
 * The old fixed 7 px line disappeared under the vehicle at driving zoom - the
 * driver couldn't tell which of two parallel carriageways was theirs. Widths
 * now grow with zoom the way Mapbox's own road layers do, so the route stays
 * proportionate to the streets under it at every scale.
 */
function widthAt(factor: number): mapboxgl.DataDrivenPropertyValueSpecification<number> {
  return [
    'interpolate',
    ['exponential', 1.4],
    ['zoom'],
    8,
    4 * factor,
    12,
    8 * factor,
    15,
    15 * factor,
    17,
    23 * factor,
    20,
    34 * factor,
  ];
}

export class MapView {
  readonly map: mapboxgl.Map;
  private puckEl: HTMLDivElement;
  private puck: mapboxgl.Marker;
  private destMarker: mapboxgl.Marker | null = null;
  private ready = false;
  private queued: Array<() => void> = [];
  private displayedHeading = 0;
  private following = true;
  private navigating = false;
  private longPressTimer: number | null = null;
  private pressOrigin: mapboxgl.Point | null = null;
  private theme: 'night' | 'day' = 'day';
  /**
   * Which name each place is labelled with. 'local' means the place's own
   * name - Thai inside Thailand - and is what the driver gets when the app is
   * set to Thai. Held here so a theme switch does not silently revert it.
   */
  private labelLang: 'en' | 'local' = 'en';
  /** Extruded 3D buildings - a settings toggle, off unless the driver opts in. */
  private buildings3d = false;
  /** Driver's tilt + zoom choices; see CameraPrefs. Set from Settings at boot. */
  private cameraPrefs: CameraPrefs = { pitch: 58, driveZoom: 0 };

  constructor(
    container: HTMLElement,
    style: object | string,
    private events: MapViewEvents = {},
  ) {
    mapboxgl.accessToken = MAPBOX_TOKEN;
    this.map = new mapboxgl.Map({
      container,
      style: style as string,
      center: REGION.fallbackCenter,
      zoom: 12,
      pitch: 0,
      bearing: 0,
      attributionControl: false,
      // We drive the camera ourselves; disable anything that fights it.
      pitchWithRotate: false,
      touchPitch: false,
      fadeDuration: 150,
    });
    this.map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');
    this.map.touchZoomRotate.disableRotation();

    // The vehicle marker: a heading arrow, drawn large. The original was a
    // ~36 px arrow inside a 52 px box and disappeared among the street
    // furniture at driving zoom; this keeps the arrow shape the driver asked
    // for but at roughly double the size, with a heavy outline so it reads
    // against both the light and dark map themes.
    this.puckEl = document.createElement('div');
    this.puckEl.className = 'puck';
    this.puckEl.innerHTML = `
      <div class="puck__halo"></div>
      <svg class="puck__arrow" viewBox="0 0 64 64" aria-hidden="true">
        <path d="M32 5 L52 53 L32 42 L12 53 Z" />
      </svg>`;
    this.puck = new mapboxgl.Marker({
      element: this.puckEl,
      rotationAlignment: 'map',
      pitchAlignment: 'map',
    })
      .setLngLat(REGION.fallbackCenter)
      .addTo(this.map);
    this.puckEl.style.visibility = 'hidden';

    this.map.on('load', () => {
      this.addLayers();
      this.ready = true;
      for (const fn of this.queued) fn();
      this.queued = [];
    });

    // If the container was mid-layout at construction, the canvas keeps
    // mapbox's tiny fallback size - re-measure on the next frames.
    requestAnimationFrame(() => this.map.resize());
    setTimeout(() => this.map.resize(), 400);

    // Any deliberate gesture breaks camera-follow until re-centred.
    const gesture = (e: { originalEvent?: Event } | unknown) => {
      if (!(e as { originalEvent?: Event }).originalEvent) return; // programmatic move
      if (this.following) {
        this.following = false;
        this.events.onUserGesture?.();
      }
    };
    this.map.on('dragstart', gesture);
    this.map.on('zoomstart', gesture);
    this.map.on('rotatestart', gesture);

    // Long-press to drop a pin. Any movement used to cancel it outright, which
    // made pinning a soi nearly impossible from a car mount - holding a finger
    // perfectly still on a vibrating dashboard is not realistic. A small
    // wobble is now tolerated; only a real pan cancels.
    this.map.on('touchstart', (e) => {
      if (e.originalEvent.touches.length !== 1) return;
      const at = e.lngLat;
      this.pressOrigin = e.point;
      this.longPressTimer = window.setTimeout(() => {
        this.longPressTimer = null;
        this.events.onLongPress?.([at.lng, at.lat]);
      }, 550);
    });
    const cancelPress = () => {
      if (this.longPressTimer !== null) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
      this.pressOrigin = null;
    };
    this.map.on('touchmove', (e) => {
      if (this.longPressTimer === null || !this.pressOrigin) return;
      const dx = e.point.x - this.pressOrigin.x;
      const dy = e.point.y - this.pressOrigin.y;
      if (Math.hypot(dx, dy) > LONG_PRESS_SLOP) cancelPress();
    });
    this.map.on('touchend', cancelPress);
    this.map.on('touchcancel', cancelPress);
    this.map.on('contextmenu', (e) => this.events.onLongPress?.([e.lngLat.lng, e.lngLat.lat]));

    // Registered once here rather than layer-bound in addLayers, which re-runs
    // on every theme switch and would stack duplicate handlers.
    this.map.on('click', (e) => this.onTripClick(e));
  }

  setLabelLanguage(lang: 'en' | 'local'): void {
    this.labelLang = lang;
  }

  /**
   * Apply the driver's tilt + zoom choices.
   *
   * While navigating the next fix picks them up through driveCamera; outside
   * navigation the tilt is applied here and now, so dragging the slider in
   * Settings visibly tilts the map behind the panel - that immediate feedback
   * is what makes five abstract snap levels choosable at all.
   */
  setCameraPrefs(prefs: CameraPrefs): void {
    this.cameraPrefs = prefs;
    if (!this.navigating) {
      this.whenReady(() => this.map.easeTo({ pitch: prefs.pitch, duration: 450 }));
    }
  }

  /** Show or hide the extruded buildings; survives theme switches. */
  setBuildings3d(on: boolean): void {
    this.buildings3d = on;
    this.whenReady(() => {
      if (on) this.addBuildings();
      else if (this.map.getLayer('wf-buildings')) this.map.removeLayer('wf-buildings');
    });
  }

  private whenReady(fn: () => void): void {
    if (this.ready) fn();
    else this.queued.push(fn);
  }

  /**
   * Id of the style's first symbol layer.
   *
   * Route lines are inserted BEFORE it so that road-number shields, street
   * names and place labels draw on top of the route instead of underneath it.
   * Without this the thick blue line sits over the very road numbers the
   * driver is trying to read - reported from the road as "the road numbers are
   * under the route, we don't see them at all".
   */
  private firstSymbolLayerId(): string | undefined {
    try {
      return this.map.getStyle()?.layers?.find((l) => l.type === 'symbol')?.id;
    } catch {
      return undefined;
    }
  }

  /**
   * Where the route line has to be inserted to sit above the roads.
   *
   * "Above the first symbol layer" is the usual advice and it is wrong here.
   * In `navigation-day-v1` the first symbol layer is `turning-feature-outline-
   * navigation` at index 29, while the actual road lines run from index 30 to
   * 95 - so anchoring to it buried the route beneath every road, tunnel and
   * bridge in the style. Reported from the road twice: "the route is not
   * visible, its under everything."
   *
   * Anchor instead to the layer immediately after the LAST road-ish line. That
   * puts the route above all carriageways and below every label and shield,
   * which is the ordering we actually want.
   */
  private routeAnchorId(): string | undefined {
    try {
      const layers = this.map.getStyle()?.layers ?? [];
      let last = -1;
      for (let i = 0; i < layers.length; i++) {
        const l = layers[i] as { type: string; 'source-layer'?: string };
        if (l.type !== 'line' && l.type !== 'fill') continue;
        if (l['source-layer'] === 'road') last = i;
      }
      if (last >= 0 && layers[last + 1]) return layers[last + 1].id;
    } catch {
      /* fall through */
    }
    return this.firstSymbolLayerId();
  }



  /**
   * Terrain relief and extruded buildings.
   *
   * Both are free on the tier this app already uses: the buildings come from
   * the `composite` vector source the style has loaded anyway (no extra
   * requests at all), and `mapbox.terrain-rgb` DEM tiles are billed as ordinary
   * tile requests against the same free allowance rather than as a paid add-on.
   *
   * Terrain is kept subtle deliberately. A dramatic exaggeration looks good in
   * a screenshot and is actively unhelpful at eye level in a car, where it tips
   * the road out of the frame on every rise. Buildings are only extruded from
   * z15 so a phone GPU is not asked to draw a city while you are on a motorway.
   */
  private addDepth(): void {
    try {
      if (!this.map.getSource(SRC_DEM)) {
        this.map.addSource(SRC_DEM, {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512,
          maxzoom: 14,
        });
      }
      this.map.setTerrain({ source: SRC_DEM, exaggeration: 1.1 });
    } catch {
      /* DEM unavailable (offline, or the token lacks it) - flat map is fine */
    }

    if (this.buildings3d) this.addBuildings();
  }

  private addBuildings(): void {
    try {
      if (this.map.getLayer('wf-buildings')) return;
      // The style already ships this source; we only add the extrusion.
      if (!this.map.getSource('composite')) return;
      // Buildings must go BENEATH the route. Added on top - which is what
      // happens if you append without a beforeId - a 55%-opaque grey slab
      // covers the route line, and the driver reported exactly that: "the
      // route is not visible, it's under everything".
      const under = this.map.getLayer('wf-route-glow')
        ? 'wf-route-glow'
        : this.routeAnchorId();
      this.map.addLayer(
        {
          id: 'wf-buildings',
          source: 'composite',
          'source-layer': 'building',
          type: 'fill-extrusion',
          minzoom: 15,
          filter: ['==', ['get', 'extrude'], 'true'],
          paint: {
            // Warm stone by day, cool slate at night - grey buildings on a
            // grey map was the whole complaint.
            'fill-extrusion-color': this.theme === 'night' ? '#39414f' : '#d9c9b4',
            'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 15, 0, 16.5, ['get', 'height']],
            'fill-extrusion-base': ['get', 'min_height'],
            // Kept faint as well as underneath: at driving zoom they are
            // context, not content.
            'fill-extrusion-opacity': 0.4,
          },
        },
        under,
      );
    } catch {
      /* style without a building layer - nothing to extrude */
    }
  }

  private addLayers(): void {
    const beforeId = this.routeAnchorId();

    /*
     * Dotted trail of the last 100 km driven. Added first so it sits beneath
     * every other overlay: it is history, never guidance, and must lose every
     * fight for attention. Zero-length dashes with round caps draw as evenly
     * spaced dots.
     */
    this.map.addSource(SRC_CRUMBS, { type: 'geojson', data: this.lastCrumbData ?? emptyFC() });
    /*
     * White dots with a thin red ring, in both themes: a slightly wider red
     * dotted line drawn under a white one. Dash gaps are measured in each
     * line's OWN width, so the red layer's gap is pre-scaled by 0.18/0.26 -
     * both then repeat at the same ground distance and stay concentric.
     */
    this.map.addLayer(
      {
        id: 'wf-crumbs-ring',
        type: 'line',
        source: SRC_CRUMBS,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#d7263d',
          'line-width': widthAt(0.26),
          'line-opacity': 0.9,
          'line-dasharray': [0, 1.8],
        },
      },
      beforeId,
    );
    this.map.addLayer(
      {
        id: 'wf-crumbs',
        type: 'line',
        source: SRC_CRUMBS,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#ffffff',
          'line-width': widthAt(0.18),
          'line-opacity': 0.9,
          'line-dasharray': [0, 2.6],
        },
      },
      beforeId,
    );

    // Alternatives go underneath the active route.
    this.map.addSource(SRC_ALTS, { type: 'geojson', data: emptyFC() });
    this.map.addLayer({
      id: 'wf-alts-casing',
      type: 'line',
      source: SRC_ALTS,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#080c14', 'line-width': widthAt(0.62), 'line-opacity': 0.75 },
    }, beforeId);
    this.map.addLayer({
      id: 'wf-alts-line',
      type: 'line',
      source: SRC_ALTS,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#6b7a94', 'line-width': widthAt(0.42), 'line-opacity': 0.9 },
    }, beforeId);

    /*
     * An invisible, much wider line purely to be clicked.
     *
     * The drawn alternative is about six pixels across at driving zoom. That is
     * the right WIDTH to look at and an unreasonable target to hit - with a
     * fingertip on a moving vehicle it is close to impossible, and even with a
     * mouse it takes aim. Mapbox hit-tests the rendered geometry, and a fully
     * transparent line is still hit-tested, so a fat transparent twin gives the
     * line a target roughly the size of a fingertip without changing how any of
     * it looks.
     */
    this.map.addLayer({
      id: 'wf-alts-hit',
      type: 'line',
      source: SRC_ALTS,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#000000',
        'line-opacity': 0,
        // Never below 24 px: a fingertip is ~9 mm, and at low zoom the drawn
        // line shrinks to nothing while the need to tap it does not.
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          8,
          24,
          12,
          28,
          16,
          38,
          20,
          46,
        ],
      },
    }, beforeId);

    /*
     * Roads we have actually driven.
     *
     * "If we can drive it, there must be a road" - and often there isn't one
     * in OpenStreetMap. The access track to a hillside site, a yard, a new
     * bypass: none of them exist on the base map, but every one of them is in
     * our own trip logs. Drawing those tracks puts them back on the map, and
     * colouring by the speed actually achieved doubles as a picture of where
     * the fleet crawls.
     *
     * Sits beneath the route so it is context, never confused with guidance.
     */
    this.map.addSource(SRC_DRIVEN, { type: 'geojson', data: this.lastDrivenData ?? emptyFC() });
    this.map.addLayer(
      {
        id: 'wf-driven',
        type: 'line',
        source: SRC_DRIVEN,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#b39af7'],
          'line-width': widthAt(0.34),
          'line-opacity': 0.75,
        },
      },
      beforeId,
    );

    /*
     * One past trip under inspection, coloured by the speed actually driven.
     * Above the ambient overlays but below the live route: reviewing history
     * must never obscure guidance. The wide transparent twin makes the line
     * tappable from a car mount, same trick as the alternatives.
     */
    this.map.addSource(SRC_TRIP, { type: 'geojson', data: this.lastTripData ?? emptyFC() });
    this.map.addLayer(
      {
        id: 'wf-trip-casing',
        type: 'line',
        source: SRC_TRIP,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#04070e', 'line-width': widthAt(0.72), 'line-opacity': 0.85 },
      },
      beforeId,
    );
    this.map.addLayer(
      {
        id: 'wf-trip-line',
        type: 'line',
        source: SRC_TRIP,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#3ddc97'],
          'line-width': widthAt(0.5),
        },
      },
      beforeId,
    );
    this.map.addLayer(
      {
        id: 'wf-trip-hit',
        type: 'line',
        source: SRC_TRIP,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#000000',
          'line-opacity': 0,
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 24, 12, 28, 16, 38, 20, 46],
        },
      },
      beforeId,
    );

    this.map.addSource(SRC_ROUTE, { type: 'geojson', data: emptyFC() });

    // Soft outer glow: lifts the route off a dark map without fattening the
    // line itself. This is what makes the road readable at a glance while
    // driving, which fixed-width 7 px never did.
    this.map.addLayer({
      id: 'wf-route-glow',
      type: 'line',
      source: SRC_ROUTE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['coalesce', ['get', 'color'], ROUTE_BLUE],
        'line-width': widthAt(1.55),
        'line-opacity': 0.22,
        'line-blur': 10,
      },
    }, beforeId);
    this.map.addLayer({
      id: 'wf-route-casing',
      type: 'line',
      source: SRC_ROUTE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#04070e', 'line-width': widthAt(1.34), 'line-opacity': 0.95 },
    }, beforeId);
    this.map.addLayer({
      id: 'wf-route-line',
      type: 'line',
      source: SRC_ROUTE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['coalesce', ['get', 'color'], ROUTE_BLUE],
        'line-width': widthAt(1),
      },
    }, beforeId);
    // A brighter core down the middle gives the line depth instead of reading
    // as a flat slab of blue.
    this.map.addLayer({
      id: 'wf-route-core',
      type: 'line',
      source: SRC_ROUTE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#ffffff',
        'line-width': widthAt(0.2),
        'line-opacity': 0.28,
      },
    }, beforeId);
    // Direction chevrons, so which way the route runs is never ambiguous.
    this.map.addLayer({
      id: 'wf-route-arrows',
      type: 'symbol',
      source: SRC_ROUTE,
      minzoom: 13,
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 90,
        'text-field': '▶',
        'text-size': ['interpolate', ['linear'], ['zoom'], 13, 10, 18, 16],
        'text-keep-upright': false,
        'text-allow-overlap': true,
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
      },
      paint: {
        'text-color': '#ffffff',
        'text-opacity': 0.75,
        'text-halo-color': 'rgba(0,0,0,0.35)',
        'text-halo-width': 1,
      },
    }, beforeId);

    this.map.addSource(SRC_DEST, { type: 'geojson', data: emptyFC() });

    // Fleet saved places: dot + label, amber for shared live locations.
    this.map.addSource(SRC_PLACES, { type: 'geojson', data: this.lastPlacesData ?? emptyFC() });
    this.map.addLayer({
      id: 'wf-places-dot',
      type: 'circle',
      source: SRC_PLACES,
      paint: {
        'circle-radius': 7,
        'circle-color': ['match', ['get', 'kind'], 'shared-location', '#f5a623', '#2fbf71'],
        'circle-stroke-width': 2.5,
        'circle-stroke-color': '#ffffff',
      },
    });
    this.map.addLayer({
      id: 'wf-places-label',
      type: 'symbol',
      source: SRC_PLACES,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 12.5,
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
        'text-optional': true,
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.75)',
        'text-halo-width': 1.4,
      },
    });
    const placeClick = (e: { features?: unknown[] }) => {
      const f = e.features?.[0] as { properties?: SavedPlaceMarker } | undefined;
      if (f?.properties) this.events.onPlaceClick?.(f.properties);
    };
    this.map.on('click', 'wf-places-dot', placeClick);
    this.map.on('click', 'wf-places-label', placeClick);

    /*
     * A/B/C badges for the offered routes, so the letters in the preview list
     * can be matched to actual lines on the map. Added without an anchor, so
     * they sit above everything - a badge that hides under a road label is a
     * badge that doesn't answer "which one is which". Tapping one selects
     * that route, same as tapping the line.
     */
    this.map.addSource(SRC_LETTERS, { type: 'geojson', data: this.lastLetterData ?? emptyFC() });
    this.map.addLayer({
      id: 'wf-route-letter-bg',
      type: 'circle',
      source: SRC_LETTERS,
      paint: {
        'circle-radius': 12,
        'circle-color': ['case', ['boolean', ['get', 'sel'], false], ROUTE_BLUE, '#46536e'],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
      },
    });
    this.map.addLayer({
      id: 'wf-route-letter-text',
      type: 'symbol',
      source: SRC_LETTERS,
      layout: {
        'text-field': ['get', 'letter'],
        'text-size': 14,
        'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': '#ffffff' },
    });
    const letterClick = (e: { features?: unknown[] }) => {
      const f = e.features?.[0] as { properties?: { routeId?: string } } | undefined;
      if (f?.properties?.routeId) this.events.onAltRouteClick?.(f.properties.routeId);
    };
    this.map.on('click', 'wf-route-letter-bg', letterClick);
    this.map.on('click', 'wf-route-letter-text', letterClick);
    this.map.on('mouseenter', 'wf-route-letter-bg', () => (this.map.getCanvas().style.cursor = 'pointer'));
    this.map.on('mouseleave', 'wf-route-letter-bg', () => (this.map.getCanvas().style.cursor = ''));

    // Bigger, readable route numbers on the map itself.
    // Road numbers now live on the green sign at the top of the screen, where
    // they are read; scaling up the ones painted on the map only crowded it.
    this.addDepth();

    // Bound to the wide transparent twin, not the drawn line.
    this.map.on('click', 'wf-alts-hit', (e) => {
      const id = e.features?.[0]?.properties?.routeId as string | undefined;
      if (id) this.events.onAltRouteClick?.(id);
    });
    this.map.on('mouseenter', 'wf-alts-hit', () => (this.map.getCanvas().style.cursor = 'pointer'));
    this.map.on('mouseleave', 'wf-alts-hit', () => (this.map.getCanvas().style.cursor = ''));

    /*
     * Widen the target further still: a tap NEAR an alternative counts.
     *
     * Even 38 px demands the finger land on the line. Mapbox will query a box
     * rather than a point, so a miss within ~22 px of the route still selects
     * it - which is the difference between "aim at the line" and "tap the route
     * you meant". Only used when the precise hit above found nothing, so it can
     * never steal a tap that was already unambiguous.
     */
    this.map.on('click', (e) => {
      if (!this.map.getLayer('wf-alts-hit')) return;
      const exact = this.map.queryRenderedFeatures(e.point, { layers: ['wf-alts-hit'] });
      if (exact.length) return; // the handler above already dealt with it
      const pad = 22;
      const box: [mapboxgl.PointLike, mapboxgl.PointLike] = [
        [e.point.x - pad, e.point.y - pad],
        [e.point.x + pad, e.point.y + pad],
      ];
      const near = this.map.queryRenderedFeatures(box, { layers: ['wf-alts-hit'] });
      const id = near[0]?.properties?.routeId as string | undefined;
      if (id) this.events.onAltRouteClick?.(id);
    });
  }

  async setTheme(theme: 'night' | 'day'): Promise<void> {
    // setStyle wipes custom sources/layers; re-add and re-draw afterwards.
    const routeData = this.lastRouteData;
    const altData = this.lastAltData;
    this.ready = false;
    document.documentElement.dataset.mapTheme = theme;
    this.theme = theme;
    // `diff: false` forces a full reload. The default diffing path tries to
    // reconcile the old style with the new one and, between two styles this
    // structurally different, quietly kept parts of the previous palette -
    // which is part of why switching to night never looked properly dark.
    // The typings mark every SetStyleOptions field required; only `diff` is
    // meaningful here, so narrow through Partial rather than widening to any.
    type SetStyleOptions = Parameters<mapboxgl.Map['setStyle']>[1];
    this.map.setStyle(
      (await loadStrippedStyle(theme, this.labelLang)) as string,
      { diff: false } as Partial<SetStyleOptions> as SetStyleOptions,
    );
    this.map.once('style.load', () => {
      this.addLayers();
      this.ready = true;
      if (routeData) this.setSourceData(SRC_ROUTE, routeData);
      if (altData) this.setSourceData(SRC_ALTS, altData);
      if (this.lastLetterData) this.setSourceData(SRC_LETTERS, this.lastLetterData);
      if (this.lastDrivenData) this.setSourceData(SRC_DRIVEN, this.lastDrivenData);
      for (const fn of this.queued) fn();
      this.queued = [];
    });
  }

  private lastRouteData: GeoJSON.FeatureCollection | null = null;
  private lastAltData: GeoJSON.FeatureCollection | null = null;
  private lastLetterData: GeoJSON.FeatureCollection | null = null;
  private lastPlacesData: GeoJSON.FeatureCollection | null = null;
  private lastDrivenData: GeoJSON.FeatureCollection | null = null;
  private lastCrumbData: GeoJSON.FeatureCollection | null = null;
  private lastTripData: GeoJSON.FeatureCollection | null = null;
  /** The trip whose track is on display, with its formatting callbacks. */
  private tripView: {
    points: TrackPoint[];
    label: (p: TrackPoint) => string;
    navLabel: string;
    onNavigate: (at: LngLat) => void;
  } | null = null;
  private tripPopup: mapboxgl.Popup | null = null;

  /** Dotted trail of recently driven ground; `lines` are ready polylines. */
  setBreadcrumbs(lines: LngLat[][]): void {
    this.lastCrumbData = {
      type: 'FeatureCollection',
      features: lines.map((coords) => ({
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: coords },
      })),
    };
    this.whenReady(() => this.setSourceData(SRC_CRUMBS, this.lastCrumbData!));
  }

  clearBreadcrumbs(): void {
    this.lastCrumbData = emptyFC();
    this.whenReady(() => this.setSourceData(SRC_CRUMBS, this.lastCrumbData!));
  }

  /**
   * Put one recorded trip on the map, speed-coloured, and make it answer taps:
   * "what time were we here and how fast" is the whole point of keeping the
   * track, so the numbers must be reachable from the line itself.
   */
  showTripTrack(
    points: TrackPoint[],
    opts: { label: (p: TrackPoint) => string; navLabel: string; onNavigate: (at: LngLat) => void },
  ): void {
    if (points.length < 2) return;
    this.tripView = { points, ...opts };

    // Same banding as the driven overlay, so colours mean the same everywhere.
    const features: GeoJSON.Feature[] = [];
    let run: Array<[number, number]> = [[points[0][0], points[0][1]]];
    let band = speedBand(points[0][3]);
    for (let i = 1; i < points.length; i++) {
      const b = speedBand(points[i][3]);
      run.push([points[i][0], points[i][1]]);
      if (b !== band || i === points.length - 1) {
        if (run.length > 1) {
          features.push({
            type: 'Feature',
            properties: { color: SPEED_COLORS[band] },
            geometry: { type: 'LineString', coordinates: run },
          });
        }
        run = [[points[i][0], points[i][1]]];
        band = b;
      }
    }
    this.lastTripData = { type: 'FeatureCollection', features };
    this.whenReady(() => this.setSourceData(SRC_TRIP, this.lastTripData!));

    const [w, s, e, n] = bounds(points.map((p) => [p[0], p[1]] as LngLat));
    this.whenReady(() =>
      this.map.fitBounds(
        [
          [w, s],
          [e, n],
        ],
        { padding: 70, pitch: 0, bearing: 0, duration: 700, maxZoom: 16 },
      ),
    );
  }

  clearTripTrack(): void {
    this.tripView = null;
    this.tripPopup?.remove();
    this.tripPopup = null;
    this.lastTripData = emptyFC();
    this.whenReady(() => this.setSourceData(SRC_TRIP, this.lastTripData!));
  }

  get tripTrackShown(): boolean {
    return this.tripView !== null;
  }

  /** Tap on the displayed trip: pop the recorded time and speed at that spot. */
  private onTripClick(e: mapboxgl.MapMouseEvent): void {
    const view = this.tripView;
    if (!view || !this.map.getLayer('wf-trip-hit')) return;
    const pad = 10;
    const box: [mapboxgl.PointLike, mapboxgl.PointLike] = [
      [e.point.x - pad, e.point.y - pad],
      [e.point.x + pad, e.point.y + pad],
    ];
    if (!this.map.queryRenderedFeatures(box, { layers: ['wf-trip-hit'] }).length) return;

    // Nearest recorded sample to the tap, in flat-earth degrees - plenty at
    // town scale, and it keeps a 6000-point scan instant.
    const cos = Math.cos((e.lngLat.lat * Math.PI) / 180);
    let best = view.points[0];
    let bestD = Infinity;
    for (const p of view.points) {
      const dx = (p[0] - e.lngLat.lng) * cos;
      const dy = p[1] - e.lngLat.lat;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }

    const el = document.createElement('div');
    el.className = 'trip-popup';
    el.innerHTML = view.label(best);
    const btn = document.createElement('button');
    btn.className = 'trip-popup__go';
    btn.textContent = view.navLabel;
    btn.addEventListener('click', () => {
      this.tripPopup?.remove();
      view.onNavigate([best[0], best[1]]);
    });
    el.appendChild(btn);

    this.tripPopup?.remove();
    this.tripPopup = new mapboxgl.Popup({ offset: 12, maxWidth: '270px' })
      .setLngLat([best[0], best[1]])
      .setDOMContent(el)
      .addTo(this.map);
  }

  /**
   * Draw previously driven tracks. Each track is split into runs of similar
   * speed so the line shows where the fleet actually moves and where it
   * crawls, rather than being one flat colour.
   */
  setDrivenTracks(
    tracks: Array<Array<[number, number, number]>>,
    mode: 'speed' | 'rough' = 'speed',
  ): void {
    const bandOf = mode === 'rough' ? roughBand : speedBand;
    const palette = mode === 'rough' ? ROUGH_COLORS : SPEED_COLORS;
    const features: GeoJSON.Feature[] = [];
    for (const track of tracks) {
      if (track.length < 2) continue;
      let run: Array<[number, number]> = [[track[0][0], track[0][1]]];
      let band = bandOf(track[0][2]);
      for (let i = 1; i < track.length; i++) {
        const b = bandOf(track[i][2]);
        run.push([track[i][0], track[i][1]]);
        if (b !== band || i === track.length - 1) {
          if (run.length > 1) {
            features.push({
              type: 'Feature',
              properties: { color: palette[band] },
              geometry: { type: 'LineString', coordinates: run },
            });
          }
          run = [[track[i][0], track[i][1]]];
          band = b;
        }
      }
    }
    this.lastDrivenData = { type: 'FeatureCollection', features };
    this.whenReady(() => this.setSourceData(SRC_DRIVEN, this.lastDrivenData!));
  }

  clearDrivenTracks(): void {
    this.lastDrivenData = emptyFC();
    this.whenReady(() => this.setSourceData(SRC_DRIVEN, this.lastDrivenData!));
  }

  /** Render the fleet's saved places. Survives theme switches. */
  setPlaces(places: SavedPlaceMarker[]): void {
    this.lastPlacesData = {
      type: 'FeatureCollection',
      features: places.map((p) => ({
        type: 'Feature',
        properties: { ...p },
        geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
      })),
    };
    this.whenReady(() => this.setSourceData(SRC_PLACES, this.lastPlacesData!));
  }

  private setSourceData(id: string, data: GeoJSON.FeatureCollection): void {
    const src = this.map.getSource(id) as mapboxgl.GeoJSONSource | undefined;
    src?.setData(data);
  }

  /* ---------------------------- position ---------------------------- */

  updatePuck(fix: Fix, snappedTo?: LngLat, headingOverride?: number): void {
    const pos = snappedTo ?? [fix.lng, fix.lat];
    this.puckEl.style.visibility = 'visible';
    this.puck.setLngLat(pos as [number, number]);

    const target = headingOverride ?? fix.heading;
    if (target != null) {
      // Ease the arrow so heading noise doesn't make it twitch.
      this.displayedHeading = lerpBearing(this.displayedHeading, target, 0.35);
      this.puck.setRotation(this.displayedHeading);
    }
    this.puckEl.classList.toggle('puck--stale', Date.now() - fix.timestamp > 8000);
  }

  /* ---------------------------- camera ---------------------------- */

  startNavigation(): void {
    this.navigating = true;
    this.following = true;
  }

  stopNavigation(): void {
    this.navigating = false;
    // Back to the browse view - which is the driver's chosen tilt, not flat.
    this.map.easeTo({ pitch: this.cameraPrefs.pitch, bearing: 0, duration: 600 });
  }

  /**
   * Put the map back on the vehicle.
   *
   * Raising `following` was all this used to do, on the assumption that
   * driveCamera() would carry the map home on the next fix. It only does that
   * while NAVIGATING - it returns early otherwise - so when the driver was
   * parked or planning, nothing moved the camera at all and the button was
   * dead. Worse, `following` going true made the button judge itself no longer
   * needed and disappear, so the one control that could find the vehicle again
   * removed itself while leaving the vehicle off screen.
   *
   * So the move happens here and now, whatever the mode. Zoom, pitch and
   * bearing are left alone: the driver chose them, and while navigating
   * driveCamera restores the full driving view on the next fix anyway.
   */
  recenter(at?: LngLat | null): void {
    this.following = true;
    if (!at) return;
    this.map.easeTo({
      center: at as [number, number],
      duration: 500,
      essential: true,
    });
  }

  get isFollowing(): boolean {
    return this.following;
  }

  /** Called on every fix while navigating. */
  driveCamera(fix: Fix, progress: NavProgress | null): void {
    if (!this.navigating || !this.following) return;
    const heading =
      progress && fix.speed != null && fix.speed < 2
        ? progress.snapped.courseOnRoute // parked/slow: hold the road's bearing
        : (fix.heading ?? progress?.snapped.courseOnRoute ?? this.displayedHeading);

    const cam = computeCamera(
      progress,
      heading,
      fix.speed ?? 0,
      this.map.getContainer().clientHeight,
      this.map.getContainer().clientWidth,
      this.cameraPrefs,
    );
    this.map.easeTo({
      center: (progress?.snapped.location ?? [fix.lng, fix.lat]) as [number, number],
      zoom: cam.zoom,
      pitch: cam.pitch,
      bearing: cam.bearing,
      padding: cam.padding,
      duration: cam.duration,
      easing: (t) => t, // linear: successive eases chain into continuous motion
      essential: true,
    });
    this.updatePuck(fix, progress?.snapped.location, heading);
  }

  jumpTo(center: LngLat, zoom = 15): void {
    this.whenReady(() => this.map.easeTo({ center: center as [number, number], zoom, duration: 800 }));
  }

  fitRoute(routes: Route[], isPortrait: boolean, sheetHeight = 300, sheetWidth = 0): void {
    this.whenReady(() => {
      const pts = routes.flatMap((r) => r.geometry.coordinates);
      if (!pts.length) return;
      const [w, s, e, n] = bounds(pts);
      this.map.fitBounds(
        [
          [w, s],
          [e, n],
        ],
        {
          padding: previewPadding(
            isPortrait,
            sheetHeight,
            this.map.getContainer().clientHeight,
            sheetWidth,
            this.map.getContainer().clientWidth,
          ),
          pitch: 0,
          bearing: 0,
          duration: 700,
          maxZoom: 16,
        },
      );
    });
  }

  /* ---------------------------- routes ---------------------------- */

  /**
   * Draw the active route. With congestion annotation present, the line is
   * split into segments coloured by traffic level - the free-tier equivalent
   * of Google's red/orange overlays.
   */
  drawRoute(route: Route | null): void {
    this.whenReady(() => {
      if (!route) {
        this.lastRouteData = emptyFC();
        this.setSourceData(SRC_ROUTE, this.lastRouteData);
        return;
      }
      const features: GeoJSON.Feature[] = [];
      const congestion = route.legs[0]?.annotation?.congestion;
      const coords = route.geometry.coordinates;

      if (congestion && congestion.length >= coords.length - 1) {
        for (const seg of mergeCongestion(coords, congestion)) {
          features.push({
            type: 'Feature',
            properties: { color: CONGESTION_COLORS[seg.level] },
            geometry: { type: 'LineString', coordinates: seg.coords },
          });
        }
      } else {
        features.push({
          type: 'Feature',
          properties: { color: CONGESTION_COLORS.low },
          geometry: route.geometry,
        });
      }
      this.lastRouteData = { type: 'FeatureCollection', features };
      this.setSourceData(SRC_ROUTE, this.lastRouteData);
    });
  }

  drawAlternatives(routes: Route[]): void {
    this.whenReady(() => {
      this.lastAltData = {
        type: 'FeatureCollection',
        features: routes.map((r) => ({
          type: 'Feature',
          properties: { routeId: r._id },
          geometry: r.geometry,
        })),
      };
      this.setSourceData(SRC_ALTS, this.lastAltData);
    });
  }

  /**
   * A/B/C badges matching the letters in the preview list.
   *
   * Each badge sits at the point of its route MOST DISTANT from every other
   * offered route. The shared trunk near origin and destination scores near
   * zero there, so the letter naturally lands where that option genuinely
   * differs - which is exactly where the eye needs it to tell them apart.
   * With fewer than two options there is nothing to distinguish; no badges.
   */
  drawRouteLetters(routes: Route[], selectedIndex: number): void {
    this.whenReady(() => {
      if (routes.length < 2) {
        this.lastLetterData = emptyFC();
        this.setSourceData(SRC_LETTERS, this.lastLetterData);
        return;
      }
      this.lastLetterData = {
        type: 'FeatureCollection',
        features: routes.map((r, i) => ({
          type: 'Feature' as const,
          properties: { letter: routeLetter(i), sel: i === selectedIndex, routeId: r._id },
          geometry: {
            type: 'Point' as const,
            coordinates: divergencePoint(
              r,
              routes.filter((_, j) => j !== i),
            ),
          },
        })),
      };
      this.setSourceData(SRC_LETTERS, this.lastLetterData);
    });
  }

  setDestination(point: LngLat | null): void {
    this.whenReady(() => {
      this.destMarker?.remove();
      this.destMarker = null;
      if (!point) return;
      const el = document.createElement('div');
      el.className = 'dest-marker';
      el.innerHTML = `<svg viewBox="0 0 24 24"><path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"/></svg>`;
      this.destMarker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat(point as [number, number])
        .addTo(this.map);
    });
  }

  /**
   * Has the map been moved away from the vehicle?
   *
   * Judged in screen space rather than by distance, because "far" depends
   * entirely on zoom: 200 m off-centre is nothing at z12 and completely off
   * the display at z18. The margin keeps the button from appearing when the
   * arrow is merely near an edge but still visible.
   */
  vehicleOffScreen(at: LngLat | null): boolean {
    if (!at || !this.ready) return false;
    try {
      const p = this.map.project(at as [number, number]);
      const c = this.map.getContainer();
      const margin = 70;
      return (
        p.x < margin ||
        p.y < margin ||
        p.x > c.clientWidth - margin ||
        p.y > c.clientHeight - margin
      );
    } catch {
      return false;
    }
  }

  resize(): void {
    this.map.resize();
  }
}

/** Speed bands for the driven-roads overlay, km/h. */
const SPEED_COLORS = ['#d7263d', '#ff8c42', '#ffd23f', '#6fe3a8', '#3ddc97'];
/** Roughness bands: green is smooth, red is punishing. */
const ROUGH_COLORS = ['#3ddc97', '#a7e05a', '#ffd23f', '#ff8c42', '#d7263d'];

function roughBand(index: number): number {
  if (index < 0) return 0; // unmeasured
  if (index < 18) return 0;
  if (index < 30) return 1;
  if (index < 42) return 2;
  if (index < 58) return 3;
  return 4;
}

function speedBand(kmh: number): number {
  if (kmh < 15) return 0;
  if (kmh < 35) return 1;
  if (kmh < 60) return 2;
  if (kmh < 90) return 3;
  return 4;
}

/** Every ~n-th coordinate of a line, cheap and evenly spread. */
function sampleLine(coords: LngLat[], n: number): LngLat[] {
  const step = Math.max(1, Math.ceil(coords.length / n));
  const out: LngLat[] = [];
  for (let i = 0; i < coords.length; i += step) out.push(coords[i]);
  return out;
}

/** The point of `route` farthest from every other route - see drawRouteLetters. */
function divergencePoint(route: Route, others: Route[]): LngLat {
  const coords = route.geometry.coordinates as LngLat[];
  const fallback = coords[Math.floor(coords.length / 2)] ?? coords[0];
  const own = sampleLine(coords, 80);
  const other = others.flatMap((o) => sampleLine(o.geometry.coordinates as LngLat[], 60));
  if (!own.length || !other.length) return fallback;

  let best = fallback;
  let bestD = -1;
  for (const c of own) {
    let min = Infinity;
    for (const o of other) {
      const d = distance(c, o);
      if (d < min) min = d;
    }
    if (min > bestD) {
      bestD = min;
      best = c;
    }
  }
  return best;
}

function emptyFC(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

/** Merge per-vertex congestion into contiguous same-level line segments. */
function mergeCongestion(
  coords: LngLat[],
  congestion: Array<CongestionSegment['level']>,
): CongestionSegment[] {
  const out: CongestionSegment[] = [];
  let current: CongestionSegment | null = null;
  for (let i = 0; i < coords.length - 1; i++) {
    const level = congestion[i] ?? 'unknown';
    if (!current || current.level !== level) {
      current = { level, coords: [coords[i], coords[i + 1]] };
      out.push(current);
    } else {
      current.coords.push(coords[i + 1]);
    }
  }
  return out;
}
